import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* The score sheet of the day — Machina's two screens, for the person running it.

   ★★ Two faces, and the hinge is which heat is open. The list answers "what still has to be
   scored"; one heat answers "what happened here". That is the same split the back office has had
   for twenty years, and it is the right one: during an event somebody walks from court to court
   with a phone, and the list is the thing they come back to.

   ★★ Which boxes a heat draws is the GAME talking, not this screen. `columns` comes off the game's
   scoring type: a tug of war measures one number, a relay two times, a quiz a score and nothing
   else. A column with no name is a thing this game does not measure, and no box is drawn for it.
   A game with no scoring type at all leaves only the rank — which is still a complete result.

   ★★ The rank is what becomes points. Everything else is the evidence. The ladder is shown beside
   the box because "what is second place worth here" is a per-event answer nobody remembers, and an
   event with no ladder scores nothing at all no matter what gets typed — so that says so, loudly.

   ★ One heat is saved at a time, whole. A per-field autosave on a screen used outdoors on mobile
   data is how half a result ends up written.

   ★★ **One heat can be asked for by name.** `?heat=<id>` opens that sheet straight away, and
   `?back=ranking` sends the way out back to the standing. That is what makes a line of the standing
   correctable: the organiser reads "that number is wrong", clicks it, and lands on the sheet that
   wrote it — rather than on a second place to type the same result, which is how two answers to
   "what happened on that court" get into the day. */

const RANK_EMPTY = '';

/* One line of the sheet, as it is being edited. `was` is what it looked like on arrival so Save
   can tell whether there is anything to write. */
const toRow = (team) => ({
  id: team.id,
  name: team.name,
  number: team.number,
  company_name: team.company_name,
  rank: team.rank == null ? RANK_EMPTY : String(team.rank),
  score_1: team.score_1 == null ? '' : String(team.score_1),
  score_2: team.score_2 == null ? '' : String(team.score_2),
  time_1: team.time_1 == null ? '' : String(team.time_1),
  time_2: team.time_2 == null ? '' : String(team.time_2),
  is_fairplay: !!team.is_fairplay,
  is_disqualified: !!team.is_disqualified,
  /* ★★ Speelt deze ploeg hier haar JOKER? Geen vinkje: de ploeg heeft dat spel vooraf gekozen en
     de server leidt het af (``joker_is_played``). Het staat in ``stamp`` niet mee, want het is
     niets dat op dit scherm veranderd kan worden — het is wat er waar is. */
  is_joker: !!team.is_joker,
});

const stamp = (r) => [r.rank, r.score_1, r.score_2, r.time_1, r.time_2,
  r.is_fairplay ? 1 : 0, r.is_disqualified ? 1 : 0].join('|');

const CompanyEventScoring = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [params] = useSearchParams();
  // Which heat was asked for, and where the way out leads. See the note at the top.
  const wanted = (params.get('heat') || '').toLowerCase();
  const backTo = params.get('back');

  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);      // the heat being scored
  const [sheet, setSheet] = useState(null);    // its payload
  const [rows, setRows] = useState([]);
  const [base, setBase] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // Tried once, whatever the outcome: a heat id that is not on this day must not re-fetch on every
  // render, and re-opening the sheet after the host has closed it would trap them on it.
  const [linked, setLinked] = useState(false);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/scoring`)
      .then(setData)
      .catch(() => navigate(`/company-event/${eventId}/tournament`));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const adopt = useCallback((res) => {
    setSheet(res);
    const next = (res.teams || []).map(toRow);
    setRows(next);
    setBase(next.map(stamp).join('~'));
  }, []);

  const openHeat = useCallback((heat) => {
    setBusy(true);
    setNotice(null);
    apiRequest('GET', `/company/event/${eventId}/scoring/${heat.id}`)
      .then((res) => { setOpen(heat); adopt(res); })
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  }, [adopt, eventId, t]);

  /* The heat somebody arrived for, opened once the list is in — the list is what names it. A heat id
     that is not on this day says so instead of silently showing the tiles: it is the one case where
     the link came from somewhere else and a blank answer explains nothing. */
  useEffect(() => {
    if (linked || !wanted || !data) return;
    setLinked(true);
    const heat = (data.heats || []).find((h) => String(h.id).toLowerCase() === wanted);
    if (!heat) {
      setNotice(t('scoringHeatNotFound'));
      return;
    }
    openHeat(heat);
  }, [data, linked, openHeat, t, wanted]);

  /* Out of the sheet: back to whoever sent us. The standing is a different screen from the list of
     tiles, and landing on the tiles after correcting a line means finding your place again. */
  const leaveHeat = () => {
    if (backTo === 'ranking') {
      navigate(`/company-event/${eventId}/ranking`);
      return;
    }
    setOpen(null);
    load();
  };

  const patch = (id, fields) => setRows((prev) =>
    prev.map((r) => (r.id === id ? { ...r, ...fields } : r)));

  const dirty = rows.map(stamp).join('~') !== base;

  const save = () => {
    setBusy(true);
    setNotice(null);
    // Every line, every time. The server only writes the fields it is sent, and sending the whole
    // sheet means a save cannot leave one team half-written because its row happened to look
    // unchanged.
    const teams = {};
    rows.forEach((r) => {
      teams[r.id] = {
        rank: r.rank,
        score_1: r.score_1, score_2: r.score_2, time_1: r.time_1, time_2: r.time_2,
        is_fairplay: r.is_fairplay,
        is_disqualified: r.is_disqualified,
      };
    });
    apiRequest('PUT', `/company/event/${eventId}/scoring/${open.id}`, { teams })
      .then((res) => { adopt(res); setNotice(t('saved')); load(); })
      .catch((e) => setNotice(apiErrorText(t, e)))
      .finally(() => setBusy(false));
  };

  if (!data) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{notice || t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const heats = data.heats || [];
  const ladder = (sheet ? sheet.ladder : data.ladder) || [];

  /* Wat de joker op deze rang opbrengt — de versnelling, in punten. 0 zolang er geen rang staat:
     de joker hangt aan de plaats die ze halen, niet aan het spelen op zich. */
  const jokerBonus = (rank) => {
    const row = ladder.find((p) => String(p.rank) === String(rank));
    return row ? Number(row.joker || 0) : 0;
  };
  const columns = (sheet && sheet.columns) || {};

  /* ---- one heat ---- */
  if (open && sheet) {
    return (
      <Base activeNav="home">
        <Card>
          <button type="button" className="sc-back" onClick={leaveHeat}>
            <Icon name="angle-left" className="me-1" />{t('back')}
          </button>
          <Title my="1" className="mt-3">
            <Icon name="star" theme="primary" className="me-2" />
            {sheet.heat.game || t('tournamentScoring')}
          </Title>
          <p className="bravos-q__hint mb-2">
            {(sheet.heat.start || '').slice(11, 16)}
            {sheet.heat.end ? ` – ${sheet.heat.end.slice(11, 16)}` : ''}
          </p>

          {notice && <p className="ce-note">{notice}</p>}

          {/* ★★ An event with no ladder scores nothing whatever is typed here, and nothing else on
              any screen says so. Loud, and with the way out on it. */}
          {ladder.length === 0 && (
            <div className="ce-note ce-note--warn">
              <p className="mb-1">{t('scoringNoLadder')}</p>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => navigate(`/company-event/${eventId}/tournament`)}>
                {t('tournamentMakeLadder')}
              </button>
            </div>
          )}

          <div className="sc-sheet">
            {rows.map((r) => (
              <div key={r.id}
                   className={`sc-row${r.is_disqualified ? ' sc-row--out' : ''}`}>
                <div className="sc-row__team">
                  <span className="sc-row__name">{r.name}</span>
                  {r.number && <span className="sc-row__num">{r.number}</span>}
                  {/* ★★ De joker van de ploeg, op dit spel. Machina's scoreblad tekent precies dit
                      — een teken, geen vinkje — en om dezelfde reden: de gastheer beslist het niet,
                      hij moet het wel WETEN, want deze plaats is voor deze ploeg meer waard. */}
                  {r.is_joker && (
                    <span className="sc-row__joker" title={t('scoringJokerHint')}>
                      <Icon name="star" /> {t('joker')}
                      {/* ★★ Wat de joker DOET: versnellen. De plaats die ze halen is voor deze
                          ploeg zoveel punten méér waard — en de versnelling is het grootst bovenaan,
                          dus ze verandert terwijl de rang gekozen wordt. Uit dezelfde ladder die
                          het vak ernaast al leest; hier wordt niets bijgerekend. */}
                      {jokerBonus(r.rank) > 0 && (
                        <b className="sc-row__joker-plus">{`+${jokerBonus(r.rank)}`}</b>
                      )}
                    </span>
                  )}
                </div>

                <div className="sc-row__fields">
                  {/* The measurement boxes the GAME asks for — and only those. */}
                  {['score_1', 'score_2', 'time_1', 'time_2'].map((field) => (
                    columns[field] ? (
                      <span className="ct-field sc-field" key={field}>
                        <label htmlFor={`${field}-${r.id}`}>{columns[field]}</label>
                        <input id={`${field}-${r.id}`} type="text" inputMode="numeric"
                               value={r[field]} disabled={busy || r.is_disqualified}
                               onChange={(e) => patch(r.id, { [field]: e.target.value })} />
                      </span>
                    ) : null
                  ))}

                  {/* ★ The rank, and it is the one box that is never optional: it is what the
                      ladder turns into points. A select rather than a number box, because the
                      answers are exactly the places this event pays. */}
                  <span className="ct-field sc-field sc-field--rank">
                    <label htmlFor={`rank-${r.id}`}>{t('scoringRank')}</label>
                    <select id={`rank-${r.id}`} value={r.rank}
                            disabled={busy || r.is_disqualified || !ladder.length}
                            onChange={(e) => patch(r.id, { rank: e.target.value })}>
                      <option value="">{t('scoringRankNone')}</option>
                      {ladder.map((p) => (
                        <option key={p.rank} value={String(p.rank)}>
                          {t('scoringRankOption', { n: p.rank, points: p.score })}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>

                <div className="sc-row__flags">
                  <label className="ce-check">
                    <input type="checkbox" checked={r.is_fairplay} disabled={busy || r.is_disqualified}
                           onChange={(e) => patch(r.id, { is_fairplay: e.target.checked })} />
                    <span>{t('scoringFairplay')}</span>
                  </label>
                  <label className="ce-check">
                    <input type="checkbox" checked={r.is_disqualified} disabled={busy}
                           onChange={(e) => patch(r.id, { is_disqualified: e.target.checked })} />
                    <span>{t('scoringDisqualified')}</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          {rows.length === 0 && <p className="ce-note">{t('scoringNoTeams')}</p>}

          <div className="ce-actions">
            <button type="button" className="bravos-submit" disabled={busy || !dirty}
                    onClick={save}>
              <Icon name="check" /> {busy ? t('pleaseWait') : t('save')}
            </button>
          </div>
          <p className="ct-field__hint">{t('scoringHint')}</p>
        </Card>
      </Base>
    );
  }

  /* ---- the list ---- */
  const done = heats.filter((h) => h.state === 'complete').length;
  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/tournament`} />
        <Title my="1" className="mt-3">
          <Icon name="star" theme="primary" className="me-2" />
          {t('tournamentScoring')}
        </Title>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {heats.length === 0 ? (
          <p className="ce-note">{t('scoringNoHeats')}</p>
        ) : (
          <>
            <p className="ct-sum">{t('scoringProgress', { done, total: heats.length })}</p>
            {ladder.length === 0 && (
              <p className="ce-note ce-note--warn">{t('scoringNoLadder')}</p>
            )}
            <ul className="sc-tiles">
              {heats.map((h) => (
                <li key={h.id}>
                  <button type="button" className={`sc-tile sc-tile--${h.state}`}
                          disabled={busy} onClick={() => openHeat(h)}>
                    <span className="sc-tile__when">{(h.start || '').slice(11, 16)}</span>
                    <span className="sc-tile__game">{h.game || h.acronym || '—'}</span>
                    <span className="sc-tile__n">{h.scored}/{h.teams}</span>
                    {/* A game with no scoring type can still be ranked, but none of its
                        measurement boxes exist — worth knowing before walking to the court. */}
                    {!h.scoring_type && (
                      <span className="sc-tile__warn">{t('scoringNoScoringType')}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyEventScoring;
